import { useMemo } from "react";
import { POSTER } from "../lib/constants";
import { lower } from "../lib/strings";

// brand-aligned poster placeholder palettes
const PALETTES = [
  { bg: "#0a0a0a", fg: "#9cff00", bar: "#9cff00", barFg: "#000" },
  { bg: "#9cff00", fg: "#000",    bar: "#000",    barFg: "#9cff00" },
  { bg: "#1f1f1f", fg: "#ffffff", bar: "#9cff00", barFg: "#000" },
  { bg: "#ffffff", fg: "#000000", bar: "#000",    barFg: "#9cff00" },
  { bg: "#2b2b2b", fg: "#9cff00", bar: "#fff",    barFg: "#000" },
  { bg: "#0f0f0f", fg: "#ffffff", bar: "#9cff00", barFg: "#000" },
];

const hash = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * POSTER.HASH_PRIME + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const getInitials = (title = "") =>
  title
    .replace(/[^a-zA-Z\s]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// poster sizes (2:3 aspect)
const SIZES = {
  sm:   { width: POSTER.WIDTH_SM, initials: POSTER.INITIALS_SM, bar: "text-[8px]"  },
  md:   { width: POSTER.WIDTH_MD, initials: POSTER.INITIALS_MD, bar: "text-[9px]"  },
  lg:   { width: POSTER.WIDTH_LG, initials: POSTER.INITIALS_LG, bar: "text-[11px]" },
  hero: { width: "100%",          initials: POSTER.INITIALS_HERO, bar: "text-xs"   },
};

export default function Poster({ show, size = "md", className = "" }) {
  const palette = useMemo(
    () => PALETTES[hash(show?.id || "x") % PALETTES.length],
    [show?.id]
  );
  const initials = useMemo(() => getInitials(show?.title), [show?.title]);
  const cfg = SIZES[size] || SIZES.md;

  const wrapperStyle =
    size === "hero"
      ? { aspectRatio: "2 / 3", width: "100%" }
      : { width: cfg.width, aspectRatio: "2 / 3" };

  if (show?.posterUrl) {
    return (
      <div
        data-testid={`poster-${show.id}`}
        className={`relative shrink-0 overflow-hidden border-2 border-white/20 ${className}`}
        style={wrapperStyle}
      >
        <img
          src={show.posterUrl}
          alt={show.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      data-testid={`poster-${show?.id || "unknown"}`}
      className={`relative shrink-0 overflow-hidden border-2 ${className}`}
      style={{
        ...wrapperStyle,
        backgroundColor: palette.bg,
        color: palette.fg,
        borderColor: palette.bar,
      }}
    >
      <div
        className="absolute top-1 left-1.5 font-mono text-[9px] uppercase tracking-widest opacity-60"
        style={{ color: palette.fg }}
      >
        {String((hash(show?.id || "x") % POSTER.CORNER_INDEX_MOD) + 1).padStart(2, "0")}
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="font-display leading-none"
          style={{ fontSize: cfg.initials, color: palette.fg, letterSpacing: "-0.04em" }}
        >
          {initials}
        </span>
      </div>

      <div
        className={`absolute bottom-0 left-0 right-0 px-1.5 py-0.5 font-mono ${cfg.bar} truncate uppercase tracking-wider`}
        style={{ backgroundColor: palette.bar, color: palette.barFg }}
      >
        {lower(show?.theatre) || "lobby"}
      </div>
    </div>
  );
}
